import React from 'react'
import { cn } from '@/lib/utils'
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { CheckIcon } from 'lucide-react'

export interface CheckboxProps extends Omit<CheckboxPrimitive.Root.Props, 'size'> {
  size?: keyof typeof SizeTypeParams
  className?: string
}

const SizeTypeParams = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-5',
} as const

const selectSize = (size: keyof typeof SizeTypeParams): string =>
  SizeTypeParams[size] ?? SizeTypeParams.md

export const Checkbox = React.forwardRef<HTMLSpanElement, CheckboxProps>(
  ({ size = 'md', className, readOnly, ...props }, ref) => (
    <CheckboxPrimitive.Root
      {...props}
      readOnly={readOnly}
      data-slot='checkbox'
      data-readonly={readOnly ? 'true' : undefined}
      className={cn(
        'peer relative flex shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs transition-shadow outline-none',
        'group-has-disabled/field:opacity-50 after:absolute after:-inset-x-3 after:-inset-y-2',
        'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary',
        'dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
        'data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary',
        readOnly &&
          'cursor-default border-transparent data-checked:border-primary/50 data-checked:bg-primary/60 data-checked:text-primary-foreground dark:data-checked:bg-primary/50',
        selectSize(size),
        className
      )}
      ref={ref}
    >
      <CheckboxPrimitive.Indicator
        data-slot='checkbox-indicator'
        className='grid place-content-center text-current transition-none [&>svg]:size-3.5'
      >
        <CheckIcon />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
)
